import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Print,
  Download,
  QrCode2,
  ViewColumn as BarcodeIcon,
} from '@mui/icons-material';
import api from '../services/api';

const BarcodeLabel = ({ item, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [barcodeUrl, setBarcodeUrl] = useState(null);
  const [qrcodeUrl, setQrcodeUrl] = useState(null);

  const generateBarcode = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/inventory/items/${item.id}/barcode`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      setBarcodeUrl(url);
    } catch (err) {
      setError('Failed to generate barcode');
      console.error('Barcode generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/inventory/items/${item.id}/qrcode`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      setQrcodeUrl(url);
    } catch (err) {
      setError('Failed to generate QR code');
      console.error('QR code generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (url, type) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_${item.sku || item.id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  React.useEffect(() => {
    generateBarcode();
    generateQRCode();
  }, [item.id]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 2 }}>
        <Box textAlign="center" mb={2}>
          <Typography variant="h6" gutterBottom>
            {item.name}
          </Typography>
          <Typography variant="h5" color="primary" fontWeight="bold">
            ${Number(item.price || 0).toFixed(2)}
          </Typography>
          {item.sku && (
            <Typography variant="body2" color="text.secondary" fontFamily="monospace">
              SKU: {item.sku}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {barcodeUrl && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BarcodeIcon />
                  <Typography variant="subtitle2">Barcode</Typography>
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'background.default',
                  }}
                >
                  <img
                    src={barcodeUrl}
                    alt="Item barcode"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                </Box>
                <Box mt={1}>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={() => downloadImage(barcodeUrl, 'barcode')}
                  >
                    Download Barcode
                  </Button>
                </Box>
              </Box>
            )}

            {qrcodeUrl && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <QrCode2 />
                  <Typography variant="subtitle2">QR Code</Typography>
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    bgcolor: 'background.default',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={qrcodeUrl}
                    alt="Item QR code"
                    style={{ maxWidth: '200px', height: 'auto' }}
                  />
                </Box>
                <Box mt={1}>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={() => downloadImage(qrcodeUrl, 'qrcode')}
                  >
                    Download QR Code
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Card>

      {item.brand && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Brand: {item.brand}
        </Typography>
      )}
      {item.condition && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Condition: {item.condition}
        </Typography>
      )}
      {item.location && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Location: {item.location}
        </Typography>
      )}
    </Box>
  );
};

export default BarcodeLabel;
